import type { RestaurantTheme } from '@/types';

/**
 * Pick a readable foreground (#1D1E1E or #FFFFFF) for text/icons sitting on
 * top of a themed fill. Uses WCAG relative luminance; the 0.35 threshold keeps
 * saturated mid-tone fills (pink, orange, red, blue) on white text — matching
 * the original light-theme look — while flipping lighter fills (yellow, amber,
 * champagne gold) to dark text.
 */
function onColor(hex: string): string {
  const h = (hex ?? '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#FFFFFF';
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L =
    0.2126 * lin(parseInt(h.slice(0, 2), 16)) +
    0.7152 * lin(parseInt(h.slice(2, 4), 16)) +
    0.0722 * lin(parseInt(h.slice(4, 6), 16));
  return L > 0.35 ? '#1D1E1E' : '#FFFFFF';
}

function luminance(hex: string): number | null {
  let h = (hex ?? '').replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(h)) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * lin(parseInt(h.slice(0, 2), 16)) +
    0.7152 * lin(parseInt(h.slice(2, 4), 16)) +
    0.0722 * lin(parseInt(h.slice(4, 6), 16))
  );
}

/** Lowest WCAG contrast of `fg` against any of `bgs`; null if a colour is unparseable. */
function worstContrast(fg: string, bgs: string[]): number | null {
  const lf = luminance(fg);
  if (lf === null) return null;
  let worst = Infinity;
  for (const bg of bgs) {
    const lb = luminance(bg);
    if (lb === null) return null;
    worst = Math.min(worst, (Math.max(lf, lb) + 0.05) / (Math.min(lf, lb) + 0.05));
  }
  return worst;
}

/**
 * DF-0424 — foreground of the footer band. The mode picks the intended ink
 * (premium: the theme's text colour; playful: the card surface, white by
 * default), but the band's gradient is free, so a premium theme with a dark
 * footer printed dark ink on it (1.03–1.62:1, three restaurants at once).
 * Keep the intended ink while it stays readable (≥ 3:1 against both stops —
 * the default playful pink-purple band sits at ~3.5:1 and must not flip);
 * otherwise fall back to whichever of dark/white reads best on both stops.
 */
function footerForeground(theme: RestaurantTheme): string {
  const premium = theme.styleMode === 'premium';
  const intended = premium ? theme.textPrimary || '#222222' : theme.cardBackground || '#FFFFFF';
  const stops = [theme.footerGradientFrom, theme.footerGradientTo];
  const current = worstContrast(intended, stops);
  if (current === null || current >= 3) return intended;
  const dark = worstContrast('#1D1E1E', stops) ?? 0;
  const light = worstContrast('#FFFFFF', stops) ?? 0;
  return dark >= light ? '#1D1E1E' : '#FFFFFF';
}

/**
 * DF-0426 — ink for the © band, which sits on `primaryTeal`, not on `primary`.
 * It used to borrow `--on-primary`, computed for another colour: Yuji's light
 * grey primary gave dark ink on its dark red band (2.33:1). The band carries
 * small type, so take whichever of dark/white contrasts more with the band
 * itself — `onColor`'s 0.35 threshold leaves 3:1 on the default teal.
 */
function bandForeground(hex: string): string {
  const dark = worstContrast('#1D1E1E', [hex]);
  const light = worstContrast('#FFFFFF', [hex]);
  if (dark === null || light === null) return '#FFFFFF';
  return dark >= light ? '#1D1E1E' : '#FFFFFF';
}

export function applyTheme(target: HTMLElement, theme: RestaurantTheme): void {
  target.style.setProperty('--mint', theme.primary);
  target.style.setProperty('--mint-deep', theme.primaryDeep);
  target.style.setProperty('--mint-teal', theme.primaryTeal);
  target.style.setProperty('--hot-pink', theme.accent);
  target.style.setProperty('--purple', theme.footerGradientFrom);
  target.style.setProperty('--magenta', theme.footerGradientTo);
  // Readable foreground for content sitting on the primary / accent fills.
  target.style.setProperty('--on-primary', onColor(theme.primary));
  target.style.setProperty('--on-accent', onColor(theme.accent));
  target.style.setProperty('--on-mint-teal', bandForeground(theme.primaryTeal));
  target.style.setProperty('--on-footer', footerForeground(theme));
  if (theme.headerBarBackground) target.style.setProperty('--header-bar-bg', theme.headerBarBackground);
  if (theme.pageBackground) target.style.setProperty('--cream', theme.pageBackground);
  if (theme.pageBackground) target.style.setProperty('--ash', theme.pageBackground);
  if (theme.cardBackground) target.style.setProperty('--paper', theme.cardBackground);
  if (theme.textPrimary) target.style.setProperty('--ink', theme.textPrimary);
  if (theme.textSecondary) target.style.setProperty('--ink-soft', theme.textSecondary);
  if (theme.textSecondary) target.style.setProperty('--ink-mute', theme.textSecondary);
  if (theme.borderColor) target.style.setProperty('--border-color', theme.borderColor);
  target.dataset.styleMode = theme.styleMode === 'premium' ? 'premium' : 'playful';
}
